<template>
  <nav
    v-if="hasMultiplePages"
    class="pagination"
    role="navigation"
    aria-label="Seitennummerierung"
    aria-describedby="pagination-desc"
  >
    <span
      id="pagination-desc"
      class="sr-only"
    >
      {{ paginationLabel }}
    </span>
    <DeactivatableLink
      :disabled="!hasPrevPage"
      :emit-only-click="emitOnlyClick"
      :to="prevPageUrl"
      @click="emitClick"
    >
      <Arrow
        direction="left"
        tag="span"
      />
      Zurück <span class="sr-only">{{ prevPageAriaLabel }}</span>
    </DeactivatableLink>

    <LinkPaginator
      class="pagination__paginator"
      :current-page="currentPage"
      :emit-only-click="emitOnlyClick"
      :total-pages="totalPages"
      :url="url"
      aria-label-prefix="Seite"
      :current-page-aria-label="currentPageAriaLabel"
      @click="emitClick"
    />

    <SelectPaginator
      class="pagination__selectbox"
      :current-page="currentPage"
      :emit-only-click="emitOnlyClick"
      :total-pages="totalPages"
      :url="url"
      aria-label-prefix="Seite"
      :current-page-aria-label="currentPageAriaLabel"
      @click="emitClick"
    />

    <DeactivatableLink
      :disabled="!hasNextPage"
      :emit-only-click="emitOnlyClick"
      :to="nextPageUrl"
      @click="emitClick"
    >
      Weiter <span class="sr-only">{{ nextPageAriaLabel }}</span>
      <Arrow
        direction="right"
        tag="span"
      />
    </DeactivatableLink>
  </nav>
</template>

<script>
import { Arrow } from '@i22/td-longtail-vue-components';
import DeactivatableLink from '~/components/deactivatable-link.vue';
import LinkPaginator from '~/components/link-paginator.vue';
import SelectPaginator from '~/components/select-paginator.vue';

export default {
  name: 'Pagination',
  components: {
    Arrow,
    DeactivatableLink,
    LinkPaginator,
    SelectPaginator,
  },
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    url: {
      type: Function,
      required: true,
    },
    emitOnlyClick: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['click'],
  data() {
    return {
      component: null,
    };
  },
  computed: {
    hasMultiplePages() {
      return this.totalPages > 1;
    },
    hasPrevPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.currentPage < this.totalPages;
    },
    prevPageUrl() {
      return this.url(this.currentPage - 1);
    },
    nextPageUrl() {
      return this.url(this.currentPage + 1);
    },
    paginationLabel() {
      return `Seite ${this.currentPage} von ${this.totalPages}.`;
    },
    prevPageAriaLabel() {
      return this.hasPrevPage
        ? `zur vorherigen Seite, Seite ${this.currentPage - 1} .`
        : '. Keine vorherige Seite verfügbar.';
    },
    nextPageAriaLabel() {
      return this.hasNextPage
        ? `zur nächsten Seite, Seite ${this.currentPage + 1} .`
        : '. Keine nächste Seite verfügbar.';
    },
    currentPageAriaLabel() {
      return `Aktuelle Seite, Seite ${this.currentPage}.`;
    },
  },
  methods: {
    emitClick(target) {
      this.$emit('click', target);
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  grid-gap: $grid-base * 2;
  user-select: none;

  --arrow-position: relative;
  --arrow-width: 0.6em;
  --arrow-height: 0.6em;

  @include mq($mq-sm) {
    grid-gap: $grid-base * 3;
  }

  &__paginator {
    @include mq(false, $mq-sm - 1) {
      display: none;
    }
  }

  &__selectbox {
    @include mq($mq-sm) {
      display: none;
    }
  }
}
</style>
